import React, { useCallback, useEffect, useState } from 'react';
import { architectAPI } from '../api';
import { toast } from 'sonner';
import { CheckCircle2, AlertTriangle, Package, Download, Lock, Unlock, ClipboardCheck, FileCheck2 } from 'lucide-react';

const STATE_LABEL = {
  ACTIVE: 'In Progress', READY_FOR_HANDOVER: 'Ready for Handover', HANDED_OVER: 'Handed Over',
  CLOSED: 'Closed', REOPENED: 'Reopened',
};
const STATE_TONE = {
  ACTIVE: 'bg-slate-100 text-slate-700', READY_FOR_HANDOVER: 'bg-amber-100 text-amber-700',
  HANDED_OVER: 'bg-sky-100 text-sky-700', CLOSED: 'bg-emerald-100 text-emerald-700',
  REOPENED: 'bg-violet-100 text-violet-700',
};
const ITEM_TONE = { COMPLETE: 'text-emerald-600', PENDING: 'text-amber-600', NOT_APPLICABLE: 'text-muted-foreground' };

export const ArchitectHandoverTab = ({ id }) => {
  const [data, setData] = useState(null);
  const [final, setFinal] = useState([]);
  const [items, setItems] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, f, l, p] = await Promise.all([
        architectAPI.completion(id), architectAPI.finalSet(id),
        architectAPI.handoverChecklist(id), architectAPI.handoverPackages(id),
      ]);
      setData(c.data); setFinal(f.data.items || []); setItems(l.data || []); setPkgs(p.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unable to load handover status');
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); await load(); }
    catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Action not allowed');
    } finally { setBusy(false); }
  };

  if (!data) return <p className="p-4 text-sm text-muted-foreground" data-testid="handover-loading">Loading completion status…</p>;
  const can = data.can || {};
  const state = data.lifecycle;
  const ready = data.ready_for_closure;
  const c = data.counters || {};

  return (
    <div className="space-y-6 p-4" data-testid="handover-tab">
      <div className="flex flex-wrap items-center gap-3">
        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATE_TONE[state] || 'bg-slate-100'}`} data-testid="handover-state">
          {STATE_LABEL[state] || state}
        </span>
        <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${ready ? 'text-emerald-600' : 'text-amber-600'}`} data-testid="handover-readiness">
          {ready ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          Ready for Closure: {ready ? 'YES' : 'NO'}
        </span>
        {state === 'CLOSED'
          ? <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Lock className="h-3 w-3" />Read-only — reopen to make changes</span>
          : <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Unlock className="h-3 w-3" />Editable</span>}
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="handover-counters">
        {[['Purchased deliverables', c.purchased_deliverables], ['Completed', c.completed_deliverables],
          ['Pending', c.pending_deliverables], ['Client approved', c.client_approved_deliverables],
          ['IFC released', c.ifc_released_deliverables], ['Open rework requests', c.open_mandatory_revision_requests],
          ['Open tasks', c.open_tasks], ['Overdue items', c.overdue_items],
          ['Awaiting client', c.pending_client_approvals], ['Missing documents', c.missing_final_documents]].map(([k, v]) => (
            <div key={k} className="rounded border p-2 text-xs" data-testid={`counter-${k.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <p className="text-muted-foreground">{k}</p><p className="text-sm font-semibold">{v ?? 0}</p>
            </div>
          ))}
      </div>

      {!ready && (
        <div className="rounded border border-amber-300 bg-amber-50/60 p-3" data-testid="handover-blockers">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700">Blockers</p>
          <ul className="space-y-1">
            {(data.blockers || []).slice(0, 12).map((b, i) => (
              <li key={i} className="text-xs text-amber-900" data-testid={`blocker-${i}`}>
                {b.item}{b.service ? ` (${b.service})` : ''} — {b.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Final drawing set</p>
        <div className="space-y-1.5">
          {final.map(r => (
            <div key={r.deliverable_id} className="flex flex-wrap items-center gap-2 rounded border p-2 text-xs" data-testid={`final-row-${r.deliverable_id}`}>
              <FileCheck2 className="h-3.5 w-3.5 text-primary" />
              <span className="font-medium">{r.deliverable}</span>
              <span className="text-muted-foreground">{r.service}</span>
              <span>Final: <b>{r.final_revision || '—'}</b></span>
              <span>IFC: <b>{r.ifc_revision || '—'}</b></span>
              <span className="text-muted-foreground">{r.approval_status} · {r.release_status}</span>
              {r.working_revision && !r.working_revision_is_final && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">Working {r.working_revision} is newer &amp; unapproved</span>
              )}
              {r.file_sha256 && <span className="text-[11px] text-muted-foreground">sha {String(r.file_sha256).slice(0, 10)}</span>}
            </div>
          ))}
          {final.length === 0 && <p className="text-xs text-muted-foreground">No deliverables in purchased scope yet.</p>}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Handover checklist</p>
          {can.manage_handover
            ? <button data-testid="checklist-generate" disabled={busy || state === 'CLOSED'}
                onClick={() => run(() => architectAPI.generateChecklist(id, {}), 'Checklist updated')}
                className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs disabled:opacity-40">
                <ClipboardCheck className="h-3 w-3" />Generate / refresh
              </button>
            : <span className="text-[11px] text-muted-foreground">Needs architect.handover.manage</span>}
        </div>
        <ul className="space-y-1">
          {items.map(i => (
            <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 rounded border p-2 text-xs" data-testid={`checklist-row-${i.id}`}>
              <span>
                <b className={ITEM_TONE[i.status]}>{i.status}</b> · {i.label}
                {i.required ? <span className="ml-1 text-[11px] text-rose-600">required</span> : <span className="ml-1 text-[11px] text-muted-foreground">optional</span>}
                {i.completed_by_name && <span className="ml-1 text-muted-foreground">— {i.completed_by_name}</span>}
              </span>
              {can.manage_handover && state !== 'CLOSED' && (
                <span className="flex gap-1">
                  {['COMPLETE', 'PENDING', 'NOT_APPLICABLE'].map(st => (
                    <button key={st} data-testid={`checklist-${st.toLowerCase()}-${i.id}`} disabled={busy || i.status === st}
                      onClick={() => run(() => architectAPI.updateChecklistItem(id, i.id, { status: st }), 'Checklist item updated')}
                      className="rounded border px-1.5 py-0.5 disabled:opacity-30">{st === 'NOT_APPLICABLE' ? 'N/A' : st.toLowerCase()}</button>
                  ))}
                </span>
              )}
            </li>
          ))}
          {items.length === 0 && <li className="text-xs text-muted-foreground">Checklist not generated yet.</li>}
        </ul>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Final handover package</p>
          {can.generate_package
            ? <button data-testid="package-generate" disabled={busy || state === 'CLOSED'}
                onClick={() => run(() => architectAPI.generatePackage(id, {}), 'Package generated')}
                className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs disabled:opacity-40">
                <Package className="h-3 w-3" />Generate version
              </button>
            : <span className="text-[11px] text-muted-foreground">Needs architect.final_package.generate</span>}
        </div>
        <ul className="space-y-1">
          {pkgs.map(p => (
            <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded border p-2 text-xs" data-testid={`package-row-${p.id}`}>
              <span>v{p.version} · {(p.items || []).length} drawings · {p.generated_by_name} · {p.status}</span>
              <a data-testid={`package-download-${p.id}`} href={architectAPI.packageDownloadUrl(id, p.id)}
                className="inline-flex items-center gap-1 rounded border px-2 py-0.5"><Download className="h-3 w-3" />ZIP</a>
            </li>
          ))}
          {pkgs.length === 0 && <li className="text-xs text-muted-foreground">No package generated yet.</li>}
        </ul>
      </div>

      <div className="flex flex-wrap gap-2 border-t pt-4">
        {can.manage_handover && (state === 'ACTIVE' || state === 'REOPENED') && (
          <button data-testid="handover-ready" disabled={busy || !ready} title={ready ? '' : 'Completion readiness must be YES'}
            onClick={() => run(() => architectAPI.markReadyForHandover(id, {}), 'Marked ready for handover')}
            className="rounded border px-3 py-1.5 text-xs disabled:opacity-40">Mark Ready for Handover</button>
        )}
        {can.manage_handover && state === 'READY_FOR_HANDOVER' && (
          <button data-testid="handover-record" disabled={busy}
            onClick={() => {
              const recipient = window.prompt('Handed over to (client / recipient name)?');
              if (!recipient) return;
              const note = window.prompt('Handover note (optional)') || undefined;
              run(() => architectAPI.recordHandover(id, { recipient, note, source: 'RECORDED_BY_CRM' }), 'Handover recorded');
            }}
            className="rounded border px-3 py-1.5 text-xs disabled:opacity-40">Record Handover</button>
        )}
        {can.close && state !== 'CLOSED' && (
          <button data-testid="project-close" disabled={busy}
            onClick={() => {
              const reason = window.prompt('Close this project? Reason:');
              if (!reason) return;
              run(() => architectAPI.closeProject(id, { reason }), 'Project closed');
            }}
            className="rounded border px-3 py-1.5 text-xs disabled:opacity-40">Close Project</button>
        )}
        {can.reopen && state === 'CLOSED' && (
          <button data-testid="project-reopen" disabled={busy}
            onClick={() => {
              const reason = window.prompt('Reopen this project? Reason:');
              if (!reason) return;
              run(() => architectAPI.reopenProject(id, { reason }), 'Project reopened');
            }}
            className="rounded border px-3 py-1.5 text-xs disabled:opacity-40">Reopen Project</button>
        )}
      </div>

      {(data.handover || data.closure) && (
        <div className="space-y-1 rounded border p-3 text-xs" data-testid="handover-record">
          {data.handover && <p>Handed over to <b>{data.handover.recipient}</b> by {data.handover.handed_over_by_name} · package v{data.handover.package_version}</p>}
          {data.closure && <p>Closed by <b>{data.closure.closed_by_name}</b>{data.closure.reason ? ` · ${data.closure.reason}` : ''}{data.closure.override ? ' · OVERRIDE' : ''}</p>}
          {(data.reopen_history || []).map((r, i) => (
            <p key={i} data-testid={`reopen-row-${i}`}>Reopened by {r.reopened_by_name} · {r.reason}</p>
          ))}
        </div>
      )}
    </div>
  );
};
